import React, { useState, useEffect } from 'react';
import useTranslationCheck from '../utils/UseTranslationCheck';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5257';

const styles = {
  header: { marginTop: 0, marginBottom: '1.5rem', textAlign: 'left', fontSize: '1.5rem', fontWeight: '800', color: '#000' },
  card: { backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '0 16px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'start', padding: '14px 0', borderBottom: '1px solid #E5E5EA', fontSize: '0.95rem' },
  rowLast: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: 'none' },
  label: { color: '#666', fontWeight: '500', flex: '0 0 65%', textAlign: 'left' },
  value: { color: '#000', fontWeight: '400', textAlign: 'right', flex: 1, wordBreak: 'break-word' },
  desgloseTitle: { fontSize: '1rem', fontWeight: '700', marginBottom: '8px', color: '#000', marginLeft: '4px' },
  innerTitle: { fontSize: '0.9rem', fontWeight: '700', color: '#333', paddingTop: '14px', marginBottom: '4px' },
  desgloseContent: { whiteSpace: 'pre-wrap', lineHeight: '1.5', color: '#333', fontSize: '0.9rem', padding: '16px 0' },
  divider: { height: '1px', backgroundColor: '#E5E5EA', margin: '0 -16px' },
  totalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', padding: '0 10px' },
  totalLabel: { fontWeight: '800', fontSize: '1.3rem', color: '#000' },
  totalValue: { fontWeight: '800', fontSize: '1.8rem', color: '#000' }
};

const ResumenCalculo = ({ data }) => {
  const isTranslated = useTranslationCheck('es');
  
  // Estado para guardar el dólar que viene de la BD
  const [dolarDb, setDolarDb] = useState(null);

  useEffect(() => {
    const fetchTipoCambio = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/Dolar`);
        
        if (response.ok) {
          const result = await response.json();
          // Intentamos obtener el valor, soportando mayúsculas o minúsculas
          const valString = result.valor || result.Valor || result.data?.valor;
          
          if (valString) {
            const valNumerico = parseFloat(valString);
            if (!isNaN(valNumerico)) {
              console.log("Dólar obtenido de BD:", valNumerico);
              setDolarDb(valNumerico);
            }
          }
        } else {
            console.error("Error respuesta API:", response.status);
        }
      } catch (error) {
        console.error("Error obteniendo tipo de cambio:", error);
      }
    };

    if (isTranslated) {
      fetchTipoCambio();
    }
  }, [isTranslated]);

  // CORRECCIÓN DE PRIORIDAD:
  // 1. Base de Datos (dolarDb)
  // 2. Data local (data.tipoCambio)
  // 3. Fallback (18.50)
  const TIPO_CAMBIO = dolarDb || data?.tipoCambio || 18.50;

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return value;

    if (isTranslated) {
      // Usamos el TIPO_CAMBIO calculado correctamente
      return (value / TIPO_CAMBIO).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    }

    return value.toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
    });
  };

  const {
    servicio,
    periodoInicio,
    periodoFin,
    criterio,
    desglose,
    detalle, 
    diasCobrables,
    total,
    tramos_tarifa,
    cantidadAplicada 
  } = data || {};

  const periodoStr = [periodoInicio, periodoFin].filter(Boolean).join(' a ');
  const textoDesglose = desglose || detalle || '';
  const tieneTramos = Array.isArray(tramos_tarifa) && tramos_tarifa.length > 0;

  const lineasCalculo = [];
  if (tieneTramos && diasCobrables > 0) {
    let diasRestantes = diasCobrables;
    let diaActualAcumulado = 1; 

    tramos_tarifa.forEach((tramo) => {
      if (diasRestantes <= 0) return;
      const tamanoTramo = tramo.dia_fin 
        ? (tramo.dia_fin - tramo.dia_inicio) + 1 
        : Infinity;
      const diasEnEsteTramo = Math.min(diasRestantes, tamanoTramo);

      if (diasEnEsteTramo > 0) {
        const subtotal = diasEnEsteTramo * tramo.tarifa * (cantidadAplicada || 1);
        const rangoFin = diaActualAcumulado + diasEnEsteTramo - 1;
        
        lineasCalculo.push({
          texto: `Costo del día ${diaActualAcumulado} al ${rangoFin} (${diasEnEsteTramo} días × ${formatCurrency(tramo.tarifa)} × ${cantidadAplicada || 1} u.)`,
          subtotal: subtotal
        });

        diasRestantes -= diasEnEsteTramo;
        diaActualAcumulado += diasEnEsteTramo;
      }
    });
  }

  return (
    <div>
      <h2 style={styles.header}>Resumen del Cálculo</h2>

      <div style={styles.card}>
        <div style={styles.row}>
          <span style={styles.label}>Servicio</span>
          <span style={styles.value}>{servicio}</span>
        </div>
        
        <div style={styles.row}>
          <span style={styles.label}>Periodo</span>
          <span style={styles.value}>{periodoStr}</span>
        </div>
        
        <div style={styles.row}>
          <span style={styles.label}>Criterio</span>
          <span style={styles.value}>{criterio}</span>
        </div>

        <div style={styles.rowLast}>
          <span style={styles.label}>Total días cobrables</span>
          <span style={styles.value}>{diasCobrables}</span>
        </div>
      </div>

      <div style={styles.desgloseTitle}>Desglose del cálculo</div>

      <div style={styles.card}>
        {lineasCalculo.length > 0 && (
          <div>
            <div style={styles.innerTitle}>Cálculo detallado por tramos</div>
            {lineasCalculo.map((linea, index) => {
              const isLast = index === lineasCalculo.length - 1;
              return (
                <div style={isLast ? styles.rowLast : styles.row} key={index}>
                  <span style={styles.label}>{linea.texto}</span>
                  <span className="notranslate" style={styles.value}>
                    {formatCurrency(linea.subtotal)}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {lineasCalculo.length > 0 && textoDesglose && (
          <div style={styles.divider}></div>
        )}

        {textoDesglose && (
          <div style={styles.desgloseContent}>
             {lineasCalculo.length > 0 && <div style={{...styles.innerTitle, paddingTop:0}}>Detalle de la operación</div>}
             {textoDesglose}
          </div>
        )}
      </div>

      <div style={styles.totalRow}>
        <span style={styles.totalLabel}>
          Total {isTranslated ? '(USD)' : '(MXN)'}
        </span>
        <span className="notranslate" style={styles.totalValue}>
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  );
};

export default ResumenCalculo;